import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  ClipboardCheck,
  Image as ImageIcon,
  LayoutDashboard,
  Megaphone,
  Receipt,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "../../convex/_generated/api";
import type { Doc } from "../../convex/_generated/dataModel";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { Balance } from "@/components/brands/balance";
import { Campaigns } from "@/components/brands/campaigns";
import { Overview } from "@/components/brands/overview";
import { BrandsPitch } from "@/components/brands/pitch";
import { Products } from "@/components/brands/products";
import { Review } from "@/components/brands/review";
import { Sales } from "@/components/brands/sales";
import { Money } from "@/components/brands/ui";
import { Splash } from "@/components/shell/splash";
import { Wordmark } from "@/components/shell/wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { said } from "@/lib/errors";
import { cn } from "@/lib/utils";

const TABS = ["overview", "campaigns", "products", "sales", "balance", "review"] as const;
type Tab = (typeof TABS)[number];

const ICONS: Record<Tab, LucideIcon> = {
  overview: LayoutDashboard,
  campaigns: Megaphone,
  products: ImageIcon,
  sales: Receipt,
  balance: Wallet,
  review: ClipboardCheck,
};

export const Route = createFileRoute("/brands")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab; topup?: "success" } => ({
    ...(TABS.includes(search.tab as Tab) && { tab: search.tab as Tab }),
    ...(search.topup === "success" && { topup: "success" as const }),
  }),
  component: BrandsPage,
});

function BrandsPage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <BrandsPitch />
      </Unauthenticated>
      <Authenticated>
        <Console />
      </Authenticated>
      <Toaster />
    </>
  );
}

function Console() {
  const { t: tc } = useTranslation();
  const mine = useQuery(api.brands.mine);
  if (mine === undefined) return <Splash subtitle={tc("status.loading")} />;
  // A reviewer needs no brand of their own to do the reviewing.
  if (!mine?.brand && !mine?.reviewer) return <Setup />;
  return <Dashboard brand={mine.brand} reviewer={mine.reviewer} />;
}

function Setup() {
  const { t } = useTranslation("brands");
  const create = useMutation(api.brands.create);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await create({ name, website });
    } catch (error) {
      toast.error(said(error, t("setup.error")));
      setSaving(false);
    }
  }

  return (
    <main className="mx-auto grid min-h-svh max-w-md content-center gap-6 px-6">
      <Wordmark size="md" />
      <div className="grid gap-2">
        <h1 className="text-xl font-semibold text-ink">{t("setup.title")}</h1>
        <p className="text-sm text-ink-2">{t("setup.body")}</p>
      </div>
      <form onSubmit={(event) => void submit(event)} className="grid gap-3.5">
        <div className="grid gap-1.5">
          <Label htmlFor="brand-name">{t("setup.name")}</Label>
          <Input
            id="brand-name"
            value={name}
            maxLength={80}
            placeholder={t("setup.namePlaceholder")}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="brand-website">{t("setup.website")}</Label>
          <Input
            id="brand-website"
            type="url"
            value={website}
            placeholder={t("setup.websitePlaceholder")}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={!name.trim() || !website.trim() || saving}>
          {t("setup.submit")}
        </Button>
      </form>
    </main>
  );
}

/** A working screen rather than a page of Ours: a sidebar, tables and numbers. */
function Dashboard({ brand, reviewer }: { brand: Doc<"brands"> | null; reviewer: boolean }) {
  const { t } = useTranslation("brands");
  const { tab: asked, topup } = Route.useSearch();
  const tabs = TABS.filter((name) => (name === "review" ? reviewer : brand !== null));
  const tab = asked && tabs.includes(asked) ? asked : tabs[0]!;

  return (
    <div className="flex min-h-svh flex-col bg-paper-2 md:flex-row">
      <aside className="flex shrink-0 flex-col border-b border-line bg-paper md:sticky md:top-0 md:h-svh md:w-56 md:border-b-0 md:border-e">
        <div className="flex h-12 items-center gap-2 border-b border-line px-4">
          <Wordmark size="sm" />
          <span className="text-xs text-ink-3">{t("title")}</span>
        </div>
        <nav className="flex gap-1 overflow-x-auto p-2 md:flex-1 md:flex-col md:overflow-visible">
          {tabs.map((name) => {
            const Icon = ICONS[name];
            return (
              <Link
                key={name}
                to="/brands"
                search={{ tab: name }}
                className={cn(
                  "flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm transition-colors",
                  tab === name
                    ? "bg-paper-3 font-medium text-ink"
                    : "text-ink-2 hover:bg-paper-2 hover:text-ink",
                )}
              >
                <Icon className="size-4" />
                {t(`nav.${name}`)}
              </Link>
            );
          })}
        </nav>
        {brand ? (
          <div className="hidden border-t border-line px-4 py-3 md:block">
            <p className="truncate text-sm font-medium text-ink">{brand.name}</p>
            <p className="text-xs text-ink-3">
              {t("balance.current")}: <Money cents={brand.balanceCents} className="text-ink" />
            </p>
          </div>
        ) : null}
      </aside>
      <main className="min-w-0 flex-1 px-5 py-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {tab === "overview" ? <Overview /> : null}
          {tab === "campaigns" && brand ? <Campaigns brand={brand} /> : null}
          {tab === "products" ? <Products /> : null}
          {tab === "sales" ? <Sales /> : null}
          {tab === "balance" && brand ? <Balance brand={brand} paid={topup === "success"} /> : null}
          {tab === "review" ? <Review /> : null}
        </div>
      </main>
    </div>
  );
}
